import {
  Component, ViewChild,
  ViewContainerRef,
  KeyValueDiffers,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  KeyValueDiffer, Injectable, Inject,
} from '@angular/core';
import { Subscription, of } from 'rxjs';
import {ClusterNodeComponent} from './cluster-node/cluster-node.component';
import {AggregateArrangementsService} from '../../services/aggregate/aggregate-arrangements.service';
import {ProductOverviewService} from '../../services/product-overview/product-overview.service';
import {DomService} from '../../services/dom-service/dom.service';
import {ProductOverviewResponse} from '../../services/product-overview/product-overview-response.model';
import {ToasterService} from 'src/app/toaster-module/toaster-service/toaster.service';
import {DetailScreenComponent} from './detail-screens-module/detail-screen/detail-screen.component';

import {DetailInfoService} from './detail-screens-module/services/details-info.service';
import {UiHelperService} from '../../services/shared/helpers/ui-helper.service';

@Component({
  selector: 'app-po-treeview',
  templateUrl: './po-treeview.component.html',
  styleUrls: ['./po-treeview.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

@Injectable()
export class PoTreeviewComponent {
  @ViewChild('po_node', {read: ViewContainerRef}) nodeContainer: ViewContainerRef;
  @ViewChild('mainDetailsScreen') mainDetailsScreen: DetailScreenComponent;
  @ViewChild('secondDetailsScreen') secondDetailsScreen: DetailScreenComponent;

  isWaiting: boolean;
  isSticky = false;
  isToasterVisible = false;
  sumOfAmountsResponse;
  hierarchyResponse;
  data;
  allExpanded: boolean;
  private children: ClusterNodeComponent[] = [];
  private difference: KeyValueDiffer<string, any>;
  private EXPAND_ALL = 'Alles openklappen';
  private COLLAPSE_ALL = 'Alles inklappen';
  centeredToaster: boolean;
  toggleState = false;

  public isActiveProductsFilterEnabled = false;

  private previousFilterState: boolean | undefined = undefined;
  private hierarchyTreeData: any[] = [];


  private readonly HOIST_CHILDREN_WHEN_PARENT_INACTIVE = false;

  constructor(@Inject(DomService) private domService: DomService,
              @Inject(ProductOverviewService) private productOverviewService: ProductOverviewService,
              @Inject(AggregateArrangementsService) private aggregateArrangementService: AggregateArrangementsService,
              @Inject(KeyValueDiffers) private differences: KeyValueDiffers,
              @Inject(ChangeDetectorRef) private changeRef: ChangeDetectorRef,
              @Inject(ToasterService) private toasterService: ToasterService,
              @Inject(DetailInfoService) private detailInfoService: DetailInfoService,
              @Inject(UiHelperService) private uiHelper: UiHelperService,
  ) {
    this.difference = this.differences.find({}).create();

    this.data = {
      product: 'Product',
      details: 'Details',
      productService: 'Product Services',
      number: 'Aantal',
      abb: 'ABB',
      arrangementNumber: 'Overeenkomstnummer',
      totalAmount: 'Saldo/Premie',
      bankCode: 'Bank',
      specifics: 'Productspecifiek',
      status: 'Status',
      relation: 'Klant/Partij',
      expandAll: this.EXPAND_ALL,
      saldoToggle: 'Toon saldo',
      activeFilter: 'Verberg inactief',
    };
  }

  ngOnInit(): void {};
  loadProductoverview(): void {
    this.productOverviewService.getHierarchyNodes().subscribe(
      (resp) => {
        this.hierarchyResponse = resp;
        this.toggleState = resp.cachedToggleState;
        this.uiHelper.setBalanceToggle(resp.cachedToggleState);
        ProductOverviewResponse.relationRestrictedDataView = resp.relationRestrictedDataFlag;
        ProductOverviewResponse.relationMarketSegmentCode = resp.relationMarketSegmentCode;

        this.hierarchyTreeData = resp.children;
        this.previousFilterState = this.isActiveProductsFilterEnabled;

        if (resp.children.length > 0) {
          this.searchNode(resp.children[0], 'onlineKeys');

        this.aggregateArrangementService
          .generateSumOfAmountKeysAndGetSumOfAmounts(resp)
          .subscribe((response) => {

            this.sumOfAmountsResponse = Array.isArray(response.amountSums)
              ? response.amountSums
              : [];

            // ✅ apply sums FIRST
            this.aggregateArrangementService.addAmountsToMatchingProductNode(
              this.hierarchyResponse,
              this.sumOfAmountsResponse
            );

            // ✅ render source must be the mutated hierarchy
            this.hierarchyTreeData = this.hierarchyResponse.children;

            // ✅ NOW render
            this.renderProductTree();

            this.isWaiting = false;
            this.changeRef.detectChanges();
          });
        }
      },
      (error) => {
        this.handleError(error);
        this.isWaiting = false;
      }
    );
  }

  private renderProductTree(): void {
    if (this.nodeContainer) {
      this.nodeContainer.clear();
    }
    this.children = [];

    const nodesToRender = this.isActiveProductsFilterEnabled
      ? this.getFilteredRootNodes()
      : this.hierarchyTreeData;

    const grouped = this.groupByParent(nodesToRender);

    Object.values(grouped).forEach(group => {
      group.forEach((node, index) => {
        const comp = this.instantiateClusterComponent(node);
        if (comp && typeof comp.initializeComponents === 'function') {
          comp.isLastChild = index === group.length - 1;
          this.children.push(comp);
        }
      });
    });
    this.changeRef.detectChanges();
    this.children.forEach(c => c.initializeComponents());
    this.retriggerExpandAll();
  }

  instantiateClusterComponent(node: any) {
    if (!this.nodeContainer) return null;

    const viewRef = this.domService.addComponentToDOM(ClusterNodeComponent, this.nodeContainer);
    const componentToReturn = viewRef?.instance;
    if (!componentToReturn) return null;

    componentToReturn.hierarchyChildren = node.children || [];
    componentToReturn.detailsComponent = this.mainDetailsScreen;
    componentToReturn.secondDetailsComponent = this.secondDetailsScreen;
    componentToReturn.expandable = Array.isArray(node.children) && node.children.length > 0;
    componentToReturn.data = node.data;
    componentToReturn.expanded = true;
    componentToReturn.nodeLevel = 1;
    componentToReturn.isLastChild = false;

    return componentToReturn;
  }

  private getFilteredRootNodes(): any[] {
    if (!Array.isArray(this.hierarchyTreeData))
    {
      return [];
    }
    return this.hierarchyTreeData
      .flatMap(n => this.filterDropInactive(n, null))
      .filter(Boolean);
  }

  private filterDropInactive(node: any, parentRef: any): any[] {
    if (!node)
    {
      return [];
    }

    const rawChildren: any[] = Array.isArray(node.children) ? node.children : [];
    const filteredChildrenArrays = rawChildren.map(c => this.filterDropInactive(c, node));
    const filteredChildren = ([] as any[]).concat(...filteredChildrenArrays);
    const isActive = this.isActiveStatus(node?.data?.statusCode);

    if (!isActive) {
      return this.HOIST_CHILDREN_WHEN_PARENT_INACTIVE
        ? filteredChildren.map(child => ({ ...child, _originParent: parentRef }))
        : [];
    }

    return [{
      ...node,
      children: filteredChildren,
      _originParent: parentRef
    }];
  }

  private groupByParent(nodes: any[]): Record<string, any[]> {
    const groups: Record<string, any[]> = {};

    nodes.forEach(node => {
      const key = node._originParent ? JSON.stringify(node._originParent.data || {}) : 'root';
      if (!groups[key])
      {
        groups[key] = [];
      }
      groups[key].push(node);
    });

    return groups;
  }

  private isActiveStatus(code: any): boolean {
    return String(code) !== '4';
  }

  ngDoCheck(): void {
    const change = this.difference.diff(this);
    if (change) {
      change.forEachChangedItem((item) => {
        // ✅ toggle branch
        if (item.key === 'isActiveProductsFilterEnabled') {
          if (item.currentValue !== this.previousFilterState) {
            this.previousFilterState = item.currentValue;
            this.renderProductTree();
          }
        }
      });
    }
  }

  private retriggerExpandAll(): void {
    if (this.allExpanded) {
      this.children.forEach((child) => {
        console.log("retriggering expand all") ;
        child.allExpanded = true; // ✅ triggers @Input() setter
      });
    }
  }

  expandOrCollapseAll(): void {
    this.allExpanded = !this.allExpanded;
    this.data.expandAll = this.allExpanded ? this.COLLAPSE_ALL : this.EXPAND_ALL;
  }

  private handleError(error: any): void {
    if (!error.error || !error.error.ListOfInfo) {
      if (error.status === 403) {
        this.toasterService.startWideErrorToast(['Je bent niet geauthoriseerd om het product overzicht te bekijken.'], null, false);
      } else {
        this.toasterService.startWideErrorToast(['Er is iets misgegaan bij het opbouwen van het productoverzicht.', error.message || ''], null, false);
      }
    } else if (error.error.ListOfInfo[0].code === 'CIV_F014') {
      this.toasterService.startWideErrorToast([
        'Er zijn geen producten beschikbaar voor deze klant.',
        'Mogelijk bestaan producten van deze klant bij een andere bank.',
        'Meld indien nodig een incident via de IT Servicedesk.'
      ], '', false);
    } else {
      this.toasterService.startWideErrorToast([
        'Er is iets misgegaan bij het opbouwen van het productoverzicht.',
        error.error.ListOfInfo[0].description
      ], error.error.ListOfInfo[0].code, false);
    }
  }
  getChildComoponents(): any[] {
    return this.children;
  }

  updateToasterStyle() {
    this.centeredToaster = true;
    this.changeRef.detectChanges();
  }

  private searchNode(po, node: string) {
    const nodes = {
      onlineKeys: 'Rabo OnlineKeys',
    };
    let childList = [];

    if (po.hasOwnProperty('children')) {
      childList = po.children;
    } else if (po instanceof Array) {
      childList = po;
    }

    return childList.forEach((item) => {
      if (item.children instanceof Array) {
        if (typeof item.data === 'object' && item.data.product === nodes[node]) {
          this.detailInfoService.processData(item, node);
        }
        this.searchNode(item.children, node);
      }
    });
  }

  public toggleBalance() {
    this.uiHelper.toggleBalance().subscribe((res) => {
      this.toggleState = this.uiHelper.isBalanceToggleOn;
      this.changeRef.detectChanges();
    });
    this.uiHelper.cacheToggleState().subscribe((res) => { });
  }

}
